/**
 * Export Service
 * 
 * Handles exporting widgets and query responses to Excel/PDF.
 * - Tables → Excel (.xlsx)
 * - Charts/Metrics → PDF
 */

import { getConnectionId, getAccessToken } from '../utils/storage';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Check if visualization type is a table
 */
export const isTableVisualization = (visualizationType: string): boolean => {
    if (!visualizationType) return true;
    return ['table', 'data_table', 'grid'].includes(visualizationType.toLowerCase());
};

/**
 * Get the export format based on visualization type
 */
export const getExportFormat = (visualizationType: string): string => {
    return isTableVisualization(visualizationType) ? 'xlsx' : 'pdf';
};

/**
 * Get the export button label based on visualization type
 */
export const getExportLabel = (visualizationType: string): string => {
    return isTableVisualization(visualizationType) ? 'Export Excel' : 'Export PDF';
};

/**
 * Download a blob as a file
 */
const downloadBlob = (blob: Blob, filename: string): void => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Export a widget to Excel or PDF
 */
export const exportWidget = async (
    widgetId: string,
    visualizationType: string,
    chartImage?: string
): Promise<void> => {
    const connectionId = getConnectionId();
    const accessToken = getAccessToken();

    if (!connectionId) {
        throw new Error('Missing connection ID. Please ensure you have an active database connection.');
    }

    if (!accessToken) {
        throw new Error('Missing authentication. Please log in again.');
    }

    const url = `${API_BASE_URL}/v1/export/widget/${widgetId}`;

    // Use POST when we have a chart image (too large for URL params)
    // Otherwise use GET for simpler requests
    let response: Response;

    // if (chartImage && !isTableVisualization(visualizationType)) {
    // POST request with chart image in body
    response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
            connection_id: parseInt(connectionId),
            // chart_image: chartImage
        })
    });
    // } else {
    //     // GET request for simple exports
    //     response = await fetch(`${url}?tenant_id=${tenantId}`, {
    //         method: 'GET',
    //         headers: {
    //             'X-API-Key': apiKey
    //         }
    //     });
    // }

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Export failed' }));
        throw new Error(error.detail || 'Export failed');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `widget_export.${getExportFormat(visualizationType)}`;

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
        }
    }

    // Download the file
    const blob = await response.blob();
    downloadBlob(blob, filename);
};

/**
 * Export query response data
 */
export const exportQueryResponse = async (
    data: any[],
    title: string,
    visualizationType: string,
    query?: string,
    chartImage?: string,
    totalRowCount?: number,
    currencyColumns?: string[],
): Promise<void> => {
    const connectionId = getConnectionId();
    const accessToken = getAccessToken();

    if (!accessToken) {
        throw new Error('Missing authentication. Please log in again.');
    }

    const response = await fetch(`${API_BASE_URL}/v1/export/query-response`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
            data,
            title,
            visualization_type: visualizationType,
            query,
            connection_id: connectionId ? parseInt(connectionId) : 0,
            chart_image: chartImage,
            total_row_count: totalRowCount ?? 0,
            currency_columns: currencyColumns ?? [],
        })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Export failed' }));
        throw new Error(error.detail || 'Export failed');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `export.${getExportFormat(visualizationType)}`;

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
        }
    }

    // Download the file
    const blob = await response.blob();
    downloadBlob(blob, filename);
};

/**
 * Export the FULL result set of a paginated NL query (every matching row, not
 * just the visible page). The backend re-runs the cached SQL server-side, so a
 * 50,000-row answer is exported without ever loading 50,000 rows in the browser.
 */
export const exportCachedQuery = async (
    pageSessionKey: string,
    processId: string,
    title: string,
    visualizationType: string,
    query?: string,
    currencyColumns?: string[],
): Promise<void> => {
    const accessToken = getAccessToken();
    if (!accessToken) {
        throw new Error('Missing authentication. Please log in again.');
    }

    const response = await fetch(`${API_BASE_URL}/v1/export/cached-query`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            page_session_key: pageSessionKey,
            process_id: processId,
            title,
            visualization_type: visualizationType,
            query,
            currency_columns: currencyColumns ?? [],
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Export failed' }));
        throw new Error(error.detail || 'Export failed');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `export.${getExportFormat(visualizationType)}`;
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
        }
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
};
